import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'

export default function TimbertechVsTrexBlog() {
  // Handle client-side navigation
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
    window.scrollTo(0, 0)
  }

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])
  

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://busybeaverconstruction.com/blog"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "TimberTech vs Trex: Which Composite Decking Is Better for Calgary Weather?",
            "item": "https://busybeaverconstruction.com/blog/timbertech-vs-trex-calgary"
          }
        ]
      },
      {
        "@type": "Article",
        "headline": "TimberTech vs Trex: Which Composite Decking Is Better for Calgary Weather?",
        "author": {
          "@type": "Organization",
          "name": "Busy Beaver Construction"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Busy Beaver Construction",
          "logo": {
            "@type": "ImageObject",
            "url": "https://busybeaverconstruction.com/assets/images/2020/03/busy-beaver-logo.png"
          }
        },
        "datePublished": "2026-06-23",
        "image": "/assets/images/Decks/Composite Decks/Azek-Vintage-Walnut.jpg"
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Is TimberTech worth the extra cost in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "For elevated or exposed decks in Calgary's climate, yes. The polymer core construction, four-sided capping, and 50-year warranty on premium lines justify the $5–$10 per sq ft material premium over comparable Trex products. The performance gap is most visible after 10-plus years on south-facing Calgary decks."
            }
          },
          {
            "@type": "Question",
            "name": "Does Trex hold up in Alberta winters?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, Trex performs adequately in Alberta winters. Trex Transcend in particular holds up well. The gap between Trex and TimberTech becomes more pronounced on elevated, exposed decks over long timeframes rather than appearing in the first few seasons."
            }
          },
          {
            "@type": "Question",
            "name": "Which composite decking has the best warranty in Canada?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "TimberTech's AZEK and Legacy lines carry 50-year fade and stain warranties — the longest available from a major composite decking manufacturer in Canada. Trex's warranty maxes at 25 years across all product lines."
            }
          },
          {
            "@type": "Question",
            "name": "Can I get TimberTech installed in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. Busy Beaver Construction is a TimberTech Platinum Pro Builder — the highest installer designation in the TimberTech program — and installs TimberTech across all Calgary quadrants and surrounding communities. Call 403-286-5400 or use our online calculator to estimate your project."
            }
          },
          {
            "@type": "Question",
            "name": "How long does composite decking last in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Quality composite decking installed correctly in Calgary lasts 25–50 years. TimberTech's premium lines are warranted for 50 years. Trex warranties 25 years. Real-world performance depends on installation quality, board orientation relative to sun exposure, and maintenance of drainage and ventilation beneath the deck."
            }
          },
          {
            "@type": "Question",
            "name": "What is a TimberTech Platinum Pro Builder?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "It is the highest installer designation awarded by TimberTech to contractors who meet strict criteria for installation volume, quality, and product expertise. Busy Beaver Construction holds Platinum Pro status, which means full manufacturer warranty support on every TimberTech install and direct access to the complete TimberTech product catalogue including AZEK."
            }
          }
        ]
      }
    ]
  };

  return (
    <main className="bg-gray-50">
      <Helmet>
        <title>TimberTech vs Trex: Best Composite Decking for Calgary Weather</title>
        <meta name="description" content="Compare TimberTech and Trex composite decking for Calgary's harsh climate. Learn about freeze-thaw durability, fade resistance, warranties, and local costs." />
        <meta name="keywords" content="TimberTech vs Trex Calgary, best composite decking Calgary, composite deck builders Calgary, Trex decking Alberta, TimberTech AZEK Calgary, composite deck costs" />
        <link rel="canonical" href="https://busybeaverconstruction.com/blog/timbertech-vs-trex-calgary" />
        <meta property="og:title" content="TimberTech vs Trex: Which Composite Decking Is Better for Calgary Weather?" />
        <meta property="og:description" content="Compare TimberTech and Trex composite decking for Calgary's harsh climate. Learn about freeze-thaw durability, fade resistance, warranties, and local costs." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://busybeaverconstruction.com/blog/timbertech-vs-trex-calgary" />
        <meta property="og:image" content="https://busybeaverconstruction.com/assets/images/Decks/Composite Decks/Azek-Vintage-Walnut.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preload" href="/assets/images/Decks/Composite Decks/Azek-Vintage-Walnut.jpg" as="image" />
      </Helmet>
      
      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[450px] md:h-[500px] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60 z-10"></div>
        <img 
          src="/assets/images/Decks/Composite Decks/Azek-Vintage-Walnut.jpg"
          alt="Premium TimberTech AZEK composite deck built by Busy Beaver Construction in Calgary"
          className="absolute inset-0 w-full h-full object-cover loading-eager"
          fetchPriority="high"
        />
        <div className="relative z-20 mt-24 text-center max-w-5xl mx-auto px-4">
          <div className="inline-block bg-[#86efac] rounded py-1 px-6 mb-6">
            <p className="text-black text-sm font-bold tracking-wider uppercase">Materials & Performance Guide</p>
          </div>
          <h1 className="text-white text-4xl md:text-5xl lg:text-7xl font-primary drop-shadow-lg mb-4 leading-tight italic">
            TimberTech vs Trex: Which Composite Decking Is Better for Calgary Weather?
          </h1>
        </div>
      </section>
      
      {/* Blog Content Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 relative">
            
            <article className="lg:col-span-8 space-y-6 text-[17px] text-gray-700 leading-relaxed font-text">
              
              <h2 className="text-3xl font-bold text-[#0f172a] mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                The Short Answer for Calgary Homeowners
              </h2>
              <p>
                For Calgary's climate, TimberTech outperforms Trex. The core reason is construction: TimberTech's capped polymer boards contain no wood fibre, which means near-zero moisture absorption during Calgary's freeze-thaw cycles. Trex uses a wood-fibre composite core that absorbs more moisture over time — a meaningful difference when temperatures swing from -40°C in January to +35°C in July. If you are investing in composite decking in Calgary, the material that handles that range without degrading is the stronger long-term choice.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                Why Calgary's Climate Makes This Decision Critical
              </h2>
              <p>
                Calgary is one of the most demanding climates for outdoor building materials in Canada. Most composite decking comparisons are written for mild Pacific Northwest or Eastern Seaboard conditions. Those comparisons do not apply here.
              </p>
              <p>What Calgary does to decking materials every year:</p>
              
              <ul className="list-disc pl-6 space-y-3 mb-6">
                <li><strong>Freeze-thaw cycles.</strong> Calgary averages over 100 freeze-thaw cycles annually. Every cycle forces moisture in and out of decking boards. Materials with higher moisture absorption rates develop micro-cracks over time, accelerating surface degradation and colour fade.</li>
                <li><strong>UV intensity.</strong> Calgary sits at 1,045m above sea level and receives over 330 days of sunshine annually. UV exposure at this elevation is significantly more intense than at sea level, accelerating fade in composite boards with weaker capping layers.</li>
                <li><strong>Temperature range.</strong> A -40°C to +35°C annual swing of 75 degrees Celsius is one of the widest in any major Canadian city. Composite boards expand and contract with temperature. Boards with tighter tolerances and stronger capping hold their shape and colour better across this range.</li>
                <li><strong>Snow load and ice.</strong> Calgary receives an average of 114cm of snow annually. Boards sitting under snow and ice for 4–5 months per year are exposed to sustained moisture contact. The board's capping system — how well it seals the core from water penetration — determines how well it survives repeated seasons.</li>
              </ul>
              <p>
                Material choice that ignores these conditions is the single most common mistake Calgary homeowners make when budgeting a deck.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                TimberTech vs Trex — Head to Head
              </h2>
              <div className="overflow-x-auto my-6 shadow-sm rounded border border-gray-200">
                <table className="w-full text-left border-collapse bg-white text-sm md:text-base">
                  <thead>
                    <tr className="bg-[#0f172a] text-white">
                      <th className="p-3 md:p-4 font-bold font-secondary">Feature</th>
                      <th className="p-3 md:p-4 font-bold font-secondary">TimberTech</th>
                      <th className="p-3 md:p-4 font-bold font-secondary">Trex</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Core construction</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Capped polymer (no wood fibre)</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Capped wood-fibre composite</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Moisture absorption</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Near zero</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Low but higher than polymer</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Freeze-thaw resistance</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Excellent</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Good</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">UV/fade resistance</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">25–50 year fade warranty</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">25 year fade warranty</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Scratch resistance</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">High (capped polymer)</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Moderate to high</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Stain resistance</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">High</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">High</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Temperature range performance</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Excellent</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Good</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Price range (materials, CAD)</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">$35–$55 per sq ft</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">$30–$50 per sq ft</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Installed cost Calgary (CAD)</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">$60–$85 per sq ft</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">$55–$80 per sq ft</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Available in Canada</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Yes, full product line</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">Yes, full product line</td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 md:p-4 font-bold text-gray-900">Warranty length</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">25–50 years depending on line</td>
                      <td className="p-3 md:p-4 font-medium text-gray-700">25 years</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                Where TimberTech Has the Edge in Calgary
              </h2>
              
              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Core construction is the deciding factor.</h3>
              <p>
                TimberTech's premium lines use capped polymer construction — the board core contains no wood fibre whatsoever. This is a fundamentally different material than wood-fibre composite. Wood fibre, even when capped, absorbs marginally more moisture than polymer. In most climates that difference is negligible. In Calgary's freeze-thaw environment, it compounds over years into measurable surface degradation.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Warranty depth.</h3>
              <p>
                TimberTech's top product lines carry 50-year fade and stain warranties. Trex's warranty maxes out at 25 years. For a Calgary homeowner building a deck they plan to own for 20-plus years, warranty terms matter — but only if the installer is authorized to back them. Busy Beaver Construction holds TimberTech Platinum Pro Builder status, the highest installer designation in the program, letting us build with absolute reliability as top <a href="/" onClick={(e) => handleNavigation(e, '/')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">Calgary Deck Builders</a>. This means manufacturer warranties are fully supported on every install we complete.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Capping system quality.</h3>
              <p>
                The cap is what protects the board core from Calgary's UV and moisture. TimberTech's four-sided capping on premium lines fully encloses the board including the underside. Trex caps three sides on most lines, leaving the bottom exposed on some products. On an elevated Calgary deck where boards are exposed to wind-driven snow and ice from below, four-sided capping is a meaningful advantage for a certified <a href="/services/decks" onClick={(e) => handleNavigation(e, '/services/decks')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">composite deck builder in Calgary</a>.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Product line depth for Calgary conditions.</h3>
              <p>
                TimberTech offers distinct product lines calibrated for different performance tiers. The AZEK line is full PVC — no composite at all — and is the strongest performer in extreme climates. The Legacy and Terrain lines offer capped composite at mid-range price points. Calgary homeowners have more material options within the TimberTech ecosystem than Trex's product structure provides.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                Where Trex Is Competitive
              </h2>
              <p>
                Trex is not a poor product. It is the best-selling composite decking brand in North America for reasons that are legitimate.
              </p>
              
              <ul className="list-disc pl-6 space-y-3 mb-6">
                <li><strong>Price.</strong> Trex materials run $30–$50 per sq ft versus TimberTech's $35–$55. On a 400 sq ft deck that difference can reach $2,000–$4,000 in materials alone. For budget-conscious Calgary homeowners that gap is real.</li>
                <li><strong>Availability.</strong> Trex is stocked by more Calgary suppliers than TimberTech. Lead times on specialty TimberTech colours and profiles can run longer, particularly during peak season from May through August.</li>
                <li><strong>Colour range.</strong> Trex's Transcend and Select lines offer a wide range of earth tones and naturalistic grain patterns that many Calgary homeowners prefer aesthetically. TimberTech's colour palette is strong but Trex has invested heavily in grain variation that mimics natural wood convincingly.</li>
                <li><strong>Performance in moderate applications.</strong> For a ground-level deck in a sheltered Calgary backyard with limited sun exposure and good drainage, the performance difference between Trex and TimberTech narrows considerably. The gap widens on exposed, elevated, south-facing decks where UV and moisture exposure are highest.</li>
              </ul>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                What a Calgary Installer Sees After 25 Years
              </h2>
              <p>
                Busy Beaver Construction has been building decks in Calgary since 1999. We have installed both TimberTech and Trex across hundreds of Calgary projects in every quadrant of the city.
              </p>
              <p>What we observe over time:</p>
              <ul className="list-disc pl-6 space-y-3 mb-6">
                <li>Trex boards on south and west-facing Calgary decks show measurable fade earlier than TimberTech boards in equivalent conditions. This is most visible on darker colour selections where fade contrast is highest. Lighter Trex colours hold better.</li>
                <li>TimberTech AZEK boards — full PVC — show the least movement across Calgary's temperature range of any composite product we install. Expansion gaps on AZEK installs remain consistent after multiple seasons in ways that wood-fibre composite boards do not always replicate.</li>
                <li>Trex Transcend, the brand's premium line, performs well in Calgary and the gap between Transcend and TimberTech Legacy narrows significantly at that tier. The meaningful performance difference is between Trex's entry and mid-range lines versus TimberTech's full polymer offerings.</li>
              </ul>
              <p>
                The single most common complaint we hear from Calgary homeowners who chose composite decking without installer guidance is colour fade on south-facing boards after 8–12 years. In every case we have investigated, the boards were either entry-level composite from either brand or were installed without adequate spacing for Calgary's temperature range. Homeowners mapping out configurations can avoid layout issues by looking through our specialized <a href="/blog/types-of-decks-calgary" onClick={(e) => handleNavigation(e, '/blog/types-of-decks-calgary')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">materials & layouts guide</a>.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                TimberTech Product Lines Available in Calgary
              </h2>
              
              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">AZEK (Full PVC)</h3>
              <p>
                No wood fibre, no composite. 100% cellular PVC with the strongest freeze-thaw and UV performance in the TimberTech lineup. Best choice for exposed, elevated, south-facing Calgary decks. Price range: $45–$55 per sq ft materials. Homeowners can dive deep into seasonal survival specifications via our architectural breakdown of the <a href="/blog/best-decking-material-calgary-winters" onClick={(e) => handleNavigation(e, '/blog/best-decking-material-calgary-winters')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">best decking material for Calgary's winters</a>.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">TimberTech Legacy</h3>
              <p>
                Capped polymer composite. Strong mid-range performer with a 50-year warranty. Most popular line we install in Calgary for balanced performance and cost. Price range: $38–$48 per sq ft materials.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">TimberTech Terrain</h3>
              <p>
                Capped composite entry point into the TimberTech line. Good performance for sheltered or ground-level Calgary applications. Price range: $35–$42 per sq ft materials.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">TimberTech PRO</h3>
              <p>
                Mid-range capped composite with enhanced scratch resistance. Suitable for Calgary decks with high foot traffic or pet use. Price range: $38–$45 per sq ft materials.
              </p>
              
              <p className="mt-6 italic text-sm">
                All pricing is in CAD and reflects materials only. Installed costs including labour range from $60–$85 per sq ft depending on project complexity.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                How to Choose Between TimberTech and Trex for Your Calgary Project
              </h2>
              
              <p className="font-bold">Choose TimberTech if:</p>
              <ul className="list-disc pl-6 space-y-3 mb-6">
                <li>Your deck is elevated, south or west-facing, or fully exposed</li>
                <li>You want the longest possible warranty with full installer support</li>
                <li>You are building a high-traffic or premium outdoor living space</li>
                <li>Budget allows $60–$85 per sq ft installed</li>
                <li>You want access to AZEK full PVC for maximum Calgary climate performance</li>
              </ul>

              <p className="font-bold">Choose Trex if:</p>
              <ul className="list-disc pl-6 space-y-3 mb-6">
                <li>Your deck is ground-level or sheltered with limited direct sun exposure</li>
                <li>Budget is the primary constraint and Trex Transcend fits your range</li>
                <li>Your preferred colour or grain pattern is only available in the Trex line</li>
                <li>Lead time on TimberTech specialty products does not fit your build schedule</li>
              </ul>

              <p>
                The honest bottom line: for most Calgary homeowners building an exposed backyard deck with a 20-plus year horizon, TimberTech's polymer construction and warranty depth justify the modest price premium. For sheltered ground-level builds on tighter budgets, Trex Transcend is a legitimate alternative.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">
                FAQ — TimberTech vs Trex Calgary
              </h2>
              
              <div className="space-y-4">
                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">Is TimberTech worth the extra cost in Calgary?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    For elevated or exposed decks in Calgary's climate, yes. The polymer core construction, four-sided capping, and 50-year warranty on premium lines justify the $5–$10 per sq ft material premium over comparable Trex products. The performance gap is most visible after 10-plus years on south-facing Calgary decks.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">Does Trex hold up in Alberta winters?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    Yes, Trex performs adequately in Alberta winters. Trex Transcend in particular holds up well. The gap between Trex and TimberTech becomes more pronounced on elevated, exposed decks over long timeframes rather than appearing in the first few seasons.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">Which composite decking has the best warranty in Canada?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    TimberTech's AZEK and Legacy lines carry 50-year fade and stain warranties — the longest available from a major composite decking manufacturer in Canada. Trex's warranty maxes at 25 years across all product lines.
                  </div>
                </details>
                
                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">Can I get TimberTech installed in Calgary?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    Yes. Busy Beaver Construction is a TimberTech Platinum Pro Builder — the highest installer designation in the TimberTech program — and installs TimberTech across all Calgary quadrants and surrounding communities. Call 403-286-5400 or use our <a href="/estimate" onClick={(e) => handleNavigation(e, '/estimate')} className="text-beaver-green font-bold hover:underline">online deck cost calculator</a> to estimate your project.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">How long does composite decking last in Calgary?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    Quality composite decking installed correctly in Calgary lasts 25–50 years. TimberTech's premium lines are warranted for 50 years. Trex warranties 25 years. Real-world performance depends on installation quality, board orientation relative to sun exposure, and maintenance of drainage and ventilation beneath the deck.
                  </div>
                </details>
                
                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">What is a TimberTech Platinum Pro Builder?</h3>
                    <span className="text-beaver-green group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    It is the highest installer designation awarded by TimberTech to contractors who meet strict criteria for installation volume, quality, and product expertise. Busy Beaver Construction holds Platinum Pro status, which means full manufacturer warranty support on every TimberTech install and direct access to the complete TimberTech product catalogue including AZEK.
                  </div>
                </details>
              </div>

              <div className="mt-16 mb-8 border-t border-gray-200 pt-12">
                <h2 className="text-3xl font-bold text-[#0f172a] mb-6 font-secondary tracking-tight">
                  Get a TimberTech Composite Deck Quote in Calgary
                </h2>
                <p className="mb-6">
                  Busy Beaver Construction has installed TimberTech composite decking across Calgary since 1999. As a Platinum Pro Builder we offer the full TimberTech product line including AZEK, Legacy, Terrain, and PRO with complete manufacturer warranty support.
                </p>
                <p className="mb-8">
                  Call us at 403-286-5400, use our <a href="/estimate" onClick={(e) => handleNavigation(e, '/estimate')} className="text-beaver-green font-bold hover:underline">online deck cost calculator</a> for an instant estimate, or <a href="/appointment" onClick={(e) => handleNavigation(e, '/appointment')} className="text-beaver-green font-bold hover:underline">book a free on-site consultation</a>. We serve all Calgary quadrants and surrounding communities.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href="/appointment" 
                    onClick={(e) => handleNavigation(e, '/appointment')}
                    className="inline-flex justify-center items-center px-8 py-4 bg-[#86efac] text-black font-bold text-[16px] rounded shadow hover:brightness-95 transition-all duration-300 font-accent tracking-wide"
                  >
                    CONTACT US TODAY
                  </a>
                  <a 
                    href="/estimate" 
                    onClick={(e) => handleNavigation(e, '/estimate')}
                    className="inline-flex justify-center items-center px-8 py-4 bg-[#0f172a] text-white font-bold text-[16px] rounded shadow hover:bg-black transition-all duration-300 font-accent tracking-wide"
                  >
                    CALCULATE YOUR PROJECT
                  </a>
                </div>
              </div>

            </article>
            
            <aside className="lg:col-span-4 lg:sticky lg:top-24 h-fit mt-8 lg:mt-6">
              <div className="bg-white rounded shadow-sm border border-gray-100 pb-6">
                <h3 className="text-[22px] font-bold mb-4 font-secondary text-[#0f172a] px-6 pt-6 tracking-tight">Project Inspiration</h3>
                
                <div className="grid grid-cols-1 gap-5 max-h-[850px] overflow-y-auto px-6 custom-scrollbar">
                  {[
                    "/assets/images/Decks/Composite Decks/Tigerwood-and-mocha-2.jpg",
                    "/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg",
                    "/assets/images/Decks/Composite Decks/Azek-Vintage-Walnut.jpg",
                    "/assets/images/Decks/Composite Decks/Timbertech-Silver-Maple.jpg",
                    "/assets/images/Decks/Composite Decks/timbertech-composite-stock.jpg"
                  ].map((src, index) => (
                    <div key={index} className="overflow-hidden h-[220px] group cursor-pointer shadow-sm border-2 border-white ring-1 ring-gray-100">
                      <img 
                        src={src}
                        alt={`Inspiration gallery image ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 loading-lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </aside>
            
          </div>
        </div>
      </section>
      
      <Footer />
    </main>
  )
}